import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { OptimizationReport, OptimizationSuggestionState } from '@monitoring-panel/types';
import type { SuggestionDecision } from '@/services/api';
import { useOptimizationQueue } from '@/hooks/useOptimizationQueue';
import type { QueueJob } from '@/store/optimizationQueue';
import { cn } from '@/lib/utils';
import { fullDate } from './format';
import { dv360Context, type Dv360Context } from './links';
import { leverLabel, priorityTag } from './labels';
import {
  ChangeCell,
  isSelectable,
  RowActions,
  SelectionBar,
  StatusCell,
  suggestionEntityUrl,
} from './SuggestionsTab';
import { asDeliveryReport } from './types';

interface CardSuggestionsProps {
  executionId: string;
  report: OptimizationReport;
}

/**
 * The suggestions of the latest report, laid out for the side panel of an
 * execution card: one stacked row per suggestion instead of a wide table, with
 * the same selection, queue and actions as the Sugerencias tab.
 */
export function CardSuggestions({ executionId, report }: CardSuggestionsProps) {
  const suggestions = report.suggestionStates;
  const { enqueue, activeByKey } = useOptimizationQueue(executionId, report.reportDate);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const links = useMemo(() => dv360Context(asDeliveryReport(report.report)), [report.report]);

  const selectable = suggestions.filter((suggestion) =>
    report.actionable && isSelectable(suggestion, activeByKey.has(suggestion.key))
  );
  // Rows that left the selectable set (decided, queued) drop out of the selection.
  const chosen = selectable.filter((suggestion) => selected.has(suggestion.key));
  const allChosen = selectable.length > 0 && chosen.length === selectable.length;

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allChosen ? new Set() : new Set(selectable.map((suggestion) => suggestion.key)));
  };
  const run = (items: OptimizationSuggestionState[], decision: SuggestionDecision) => {
    if (items.length === 0) return;
    enqueue(items, decision);
    setSelected((current) => {
      const next = new Set(current);
      for (const item of items) next.delete(item.key);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {report.summary && (
        <p className="rounded-adv-md bg-adv-gold-crema px-3 py-2 text-adv-body-s text-adv-primary">{report.summary}</p>
      )}

      {!report.actionable && (
        <div className="adv-alert text-adv-body-s">
          El último informe es del {fullDate(report.reportDate)}: sus sugerencias ya no se pueden aplicar.
        </div>
      )}

      {suggestions.length === 0 ? (
        <p className="text-adv-body-s text-adv-tertiary py-2">Sin cambios propuestos: KPIs en banda y entrega cubierta.</p>
      ) : (
        <>
          {selectable.length > 0 && (
            <label className="flex items-center gap-2 px-3 text-adv-body-s text-adv-secondary cursor-pointer w-fit">
              <input type="checkbox" className="adv-check" checked={allChosen} onChange={toggleAll} />
              Seleccionar las pendientes (<span className="adv-num">{selectable.length}</span>)
            </label>
          )}
          <SelectionBar chosen={chosen} onRun={run} onClear={() => setSelected(new Set())} />
          <ul className="border border-adv-border rounded-adv-lg bg-adv-card divide-y divide-adv-border-subtle">
            {suggestions.map((suggestion) => (
              <CardSuggestionRow
                key={suggestion.key}
                suggestion={suggestion}
                job={activeByKey.get(suggestion.key) ?? null}
                actionable={report.actionable}
                selected={selected.has(suggestion.key)}
                links={links}
                onToggle={() => toggle(suggestion.key)}
                onRun={(decision) => run([suggestion], decision)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

interface CardSuggestionRowProps {
  suggestion: OptimizationSuggestionState;
  job: QueueJob | null;
  actionable: boolean;
  selected: boolean;
  links: Dv360Context;
  onToggle: () => void;
  onRun: (decision: SuggestionDecision) => void;
}

/** A suggestion without before/after (KPI notes) is described by its text alone. */
function hasValues(suggestion: OptimizationSuggestionState): boolean {
  return suggestion.before !== null || suggestion.after !== null;
}

/** A pending suggestion with nothing queued shows its actions; anything else shows its state. */
function showsStatus(suggestion: OptimizationSuggestionState, job: QueueJob | null): boolean {
  return suggestion.lever === 'kpi' || suggestion.status !== 'pending' || job !== null;
}

function CardSuggestionRow({ suggestion, job, actionable, selected, links, onToggle, onRun }: CardSuggestionRowProps) {
  const priority = priorityTag(suggestion.priority);
  const canSelect = actionable && isSelectable(suggestion, job !== null);
  const withValues = hasValues(suggestion);

  return (
    <li className={cn('flex gap-3 px-3 py-2', selected && 'bg-adv-sunken')}>
      <div className="w-4 flex-shrink-0 pt-0.5">
        {suggestion.lever !== 'kpi' && (
          <input
            type="checkbox"
            className="adv-check"
            checked={selected && canSelect}
            disabled={!canSelect}
            onChange={onToggle}
            aria-label={`Seleccionar ${suggestion.entity_name ?? suggestion.text}`}
          />
        )}
      </div>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-adv-body-s">
          <span className={`adv-tag ${priority.className}`}>{priority.label}</span>
          <span className="font-bold">{leverLabel(suggestion.lever, suggestion.bid_type)}</span>
          {suggestion.kind && <span className="text-adv-tertiary">{suggestion.kind}</span>}
        </div>
        {withValues ? <EntityName suggestion={suggestion} links={links} /> : <p className="break-words">{suggestion.text}</p>}
        <p className="text-adv-body-s text-adv-secondary line-clamp-2" title={suggestion.reason}>
          {suggestion.reason}
        </p>
        {showsStatus(suggestion, job) && <StatusCell suggestion={suggestion} job={job} />}
      </div>

      {suggestion.lever !== 'kpi' && (
        <div className="flex-shrink-0 flex flex-col items-end text-right max-w-[45%]">
          {withValues && <ChangeCell suggestion={suggestion} />}
          <RowActions suggestion={suggestion} job={job} actionable={actionable} onRun={onRun} />
        </div>
      )}
    </li>
  );
}

function EntityName({ suggestion, links }: { suggestion: OptimizationSuggestionState; links: Dv360Context }) {
  const name = suggestion.entity_name ?? 'Campaña';
  const url = suggestionEntityUrl(suggestion, links);
  if (!url) return <p className="break-words">{name}</p>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block break-words hover:underline" title="Abrir en DV360">
      {name}
      <ExternalLink size={12} className="inline ml-1 align-baseline text-adv-tertiary" />
    </a>
  );
}
